import { useHealthblocksSWR, useSubjectId } from '@healthblocks-io/core'
import { useEffect, useMemo } from 'react'

import { useAppointments } from '@/modules/appointment/hooks/useAppointments'
import { useEncounters } from '@/modules/encounter/hooks/useEncounters'

import type { CareplanActivity } from '@healthblocks-io/core'
import type { Appointment } from '@/modules/appointment/types/appointment.types'
import type { MessageResponse } from '@/modules/messages/types/message.types'
import type { TaskResponse } from '@/modules/task/types/task.types'

/**
 * Returns activities by trigger
 * @param trigger - The trigger to get activities for
 * @returns The activities by trigger
 */
export function useRelatedTasks(trigger?: string) {
  const subjectId = useSubjectId()
  return useHealthblocksSWR<TaskResponse>(
    subjectId && trigger
      ? '/task?' +
          new URLSearchParams({
            subjectId: subjectId.toString(),
            trigger,
            limit: '100',
          })
      : null,
  )
}

/**
 * Returns active document flows by trigger
 * @param trigger - The trigger to get active document flows for
 * @returns The active document flows by trigger
 */
export function useRelatedMessages(trigger?: string) {
  const subjectId = useSubjectId()
  return useHealthblocksSWR<MessageResponse>(
    subjectId && trigger
      ? '/message?' +
          new URLSearchParams({
            subjectId: subjectId.toString(),
            trigger,
            limit: '100',
          })
      : null,
  )
}

export function useRelatedDocuments(trigger?: string) {
  const subjectId = useSubjectId()
  const parameters: Record<string, string> = {
    type: 'Message',
    subjectId: subjectId?.toString() || '',
    actorRole: 'subject',
  }
  if (trigger) parameters.trigger = trigger
  const { data, isLoading } = useHealthblocksSWR<
    CareplanActivity<
      'Message',
      {
        attachments?: {
          name?: string
          url?: string
          type?: 'pdf' | 'link'
          documentId?: number
        }[]
        messageType?: 'Document' | 'Brochure' | 'Action'
      }
    >[]
  >(subjectId ? '/careplan-activity?' + new URLSearchParams(parameters) : null)

  return useMemo(() => {
    return {
      data: data?.filter(
        (activity) =>
          (activity?.meta?.messageType === 'Document' || activity?.meta?.messageType === 'Brochure') &&
          activity.status !== 'expired',
      ),
      isLoading,
    }
  }, [data, isLoading])
}

/**
 * Returns appointments by appointmentId
 * @param appointmentId - The appointmentId to get appointments for
 * @returns The appointments by appointmentId
 */
export const useRelatedAppointments = (appointmentId?: number) => {
  const {
    getAppointments: { execute: getAppointments, value: getAppointmentsValue, isLoading },
  } = useAppointments()

  // Template variables
  // ------------------------------------------------------------------------- /
  const appointment = useMemo(() => {
    if (getAppointmentsValue && appointmentId) {
      return (getAppointmentsValue as Appointment[]).find(
        (appointment) => appointment.slug === `synops_${appointmentId}`,
      )
    }
  }, [getAppointmentsValue, appointmentId])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (appointmentId) {
      getAppointments()
    }
  }, [appointmentId])

  return { appointment, isLoading }
}

/**
 * Returns encounters by trigger
 * @param trigger - The trigger to get encounters for
 * @returns The encounters by trigger
 */
export const useRelatedEncounters = (trigger?: string) => {
  const {
    getEncountersByTrigger: { execute: getEncounterByTrigger, value: getEncounterByIdValue, isLoading },
  } = useEncounters()

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (trigger) {
      getEncounterByTrigger(trigger)
    }
  }, [trigger])

  return { encounters: getEncounterByIdValue, isLoading }
}
